import React, { useState } from "react";
import { Alert } from "reactstrap";
import { Button } from "@mui/material";
import "./LifeguardPage.scss";
import Recaptcha from "react-recaptcha";
import {
  validateEmail,
  validateNumber,
  validateName,
  validateTextField,
} from "../../utilities/ValidationHelper";
import HomepageNavbar from "../../components/HomepageNavbar/HomepageNavbar";
import AlertStrings from "../../strings/AlertStrings.json";
import GeneralStrings from "../../strings/GeneralStrings.json";
import GeneralConst from "../../consts/GeneralConst";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Helmet } from "react-helmet";
import Footer from "../../components/Footer/Footer";
import LifeguardService from "../../services/LifeguardService";

const LifeguardPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [sentMessage, setSentMessage] = useState(false);
  const [failedMessage, setFailedMessage] = useState(false);
  const [recaptchaLoad, setRecaptchaLoad] = useState(false);
  const STATUS_CODES = GeneralConst.STATUS_CODES;
  const LIFEGUARD_STRINGS = GeneralStrings.LIFEGUARD;
  const TITLE = `${LIFEGUARD_STRINGS.BANNER_HEADING} | ${GeneralConst.SITE_NAME}`;

  const recaptchaLoaded = () => {
    setRecaptchaLoad(true);
  };

  const verifiedRecaptcha = (response) => {
    if (response) {
      setIsVerified(true);
    }
  };

  const resetErrorMessages = () => {
    setSentMessage(false);
    setFailedMessage(false);
  };

  const handleSubmit = (formValues) => {
    setIsLoading(true);
    resetErrorMessages();

    if (recaptchaLoad && isVerified) {
      LifeguardService.submitLifeguardRequest(formValues)
        .then((response) => {
          if (response.data.status === STATUS_CODES.ERROR) {
            setFailedMessage(
              response?.data?.statusMessage || AlertStrings.GENERAL_ERROR
            );
          } else {
            setSentMessage(
              response?.data?.statusMessage || AlertStrings.GENERAL_SUCCESS
            );
          }
        })
        .catch((response) => {
          setFailedMessage(
            response?.data?.statusMessage || AlertStrings.GENERAL_ERROR
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    setIsLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <div className="lifeguard-page">
        <HomepageNavbar transparentNavbar={false} />
        <section className="page-banner">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-8 col-xl-7">
                <div className="banner-heading">
                  <h1>{LIFEGUARD_STRINGS.BANNER_HEADING}</h1>
                  <p className="banner-subheading">
                    {LIFEGUARD_STRINGS.BANNER_SUBTEXT}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="container">
          <div className="row lifeguard-content">
            <div className="col-sm-12">
              <div className="lifeguard-item">
                <span className="fa-stack fa-4x">
                  <i className="fas fa-circle fa-stack-2x circle-pad"></i>
                  <i className="fa-solid fa-life-ring fa-stack-1x fa-inverse"></i>
                </span>
                <h4 className="my-3">{LIFEGUARD_STRINGS.LIFEGUARDING}</h4>
                <h6>{LIFEGUARD_STRINGS.LIFEGUARDING_PRICE}</h6>
                <p>{LIFEGUARD_STRINGS.LIFEGUARDING_DESCRIPTION}</p>
              </div>
            </div>
          </div>
          <hr />
          <div className="form-container">
            <h3 className="header">{LIFEGUARD_STRINGS.FORM_HEADER}</h3>
            {failedMessage && <Alert color="danger">{failedMessage}</Alert>}
            {sentMessage && <Alert>{sentMessage}</Alert>}
            <Formik
              className="form"
              initialValues={{
                name: "",
                email: "",
                numberOfSwimmers: "",
                numberOfLifeguards: "",
                eventDate: "",
                eventTime: "",
                eventLocation: "",
              }}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              <Form className="row">
                <div className="form-group col-sm-12 col-md-6">
                  <Field
                    name="name"
                    className="form-control"
                    placeholder={GeneralStrings.FORM.FORM_LABELS.YOUR_NAME}
                    validate={validateName}
                  />
                  <ErrorMessage
                    component="span"
                    className="form-error"
                    name="name"
                  />
                </div>
                <div className="form-group col-sm-12 col-md-6">
                  <Field
                    name="email"
                    className="form-control"
                    placeholder={GeneralStrings.FORM.FORM_LABELS.YOUR_EMAIL}
                    validate={validateEmail}
                  />
                  <ErrorMessage
                    component="span"
                    className="form-error"
                    name="email"
                  />
                </div>
                <div className="form-group col-sm-12 col-md-6">
                  <Field
                    name="numberOfSwimmers"
                    type="number"
                    className="form-control"
                    placeholder={
                      GeneralStrings.FORM.FORM_LABELS.NUMBER_OF_SWIMMERS
                    }
                    validate={validateNumber}
                  />
                  <ErrorMessage
                    component="span"
                    className="form-error"
                    name="numberOfSwimmers"
                  />
                </div>
                <div className="form-group col-sm-12 col-md-6">
                  <Field
                    name="numberOfLifeguards"
                    className="form-control"
                    type="number"
                    placeholder={
                      GeneralStrings.FORM.FORM_LABELS
                        .NUMBER_OF_LIFEGUARDS_NEEDED
                    }
                    validate={validateNumber}
                  />
                  <ErrorMessage
                    component="span"
                    className="form-error"
                    name="numberOfLifeguards"
                  />
                </div>
                <div className="form-group col-sm-12 col-md-4">
                  <Field
                    name="eventDate"
                    type="date"
                    className="form-control"
                    placeholder={GeneralStrings.FORM.FORM_LABELS.EVENT_LOCATION}
                    validate={validateTextField}
                  />
                  <ErrorMessage
                    component="span"
                    className="form-error"
                    name="eventDate"
                  />
                </div>
                <div className="form-group col-sm-12 col-md-4">
                  <Field
                    name="eventTime"
                    type="time"
                    className="form-control"
                    placeholder={GeneralStrings.FORM.FORM_LABELS.EVENT_LOCATION}
                    validate={validateTextField}
                  />
                  <ErrorMessage
                    component="span"
                    className="form-error"
                    name="eventTime"
                  />
                </div>
                <div className="form-group col-sm-12 col-md-4">
                  <Field
                    name="eventLocation"
                    className="form-control"
                    placeholder={GeneralStrings.FORM.FORM_LABELS.EVENT_LOCATION}
                    validate={validateTextField}
                  />
                  <ErrorMessage
                    component="span"
                    className="form-error"
                    name="eventLocation"
                  />
                </div>
                <div className="form-group">
                  <Recaptcha
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    render="explicit"
                    onloadCallback={recaptchaLoaded}
                    verifyCallback={verifiedRecaptcha}
                  />
                </div>
                <div className="form-group btn-container">
                  <Button
                    variant="contained"
                    disabled={isLoading}
                    loading={isLoading}
                    className="submit-btn"
                    type="submit"
                  >
                    {GeneralStrings.SUBMIT_REQUEST}
                  </Button>
                </div>
              </Form>
            </Formik>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default LifeguardPage;
